import { motion } from "framer-motion";
import { MapPin, Phone, Clock, ArrowUpRight } from "lucide-react";
import { BOOKING_URL, CONTACT, IMAGES } from "../data/content";
import { useT } from "../i18n/LanguageContext";

export default function Contact() {
  const { t } = useT();

  const items = [
    {
      icon: MapPin,
      label: t("contact.card_studio_label"),
      primary: "Hainer Weg 146",
      secondary: "60599 Frankfurt am Main",
      href: "https://www.google.com/maps/search/?api=1&query=Hainer+Weg+146+Frankfurt",
      cta: t("contact.card_route"),
      testid: "contact-address",
    },
    {
      icon: Phone,
      label: t("contact.card_phone_label"),
      primary: CONTACT.phone,
      secondary: t("contact.card_phone_subtitle"),
      href: `tel:${CONTACT.phoneLink}`,
      cta: t("contact.card_call"),
      testid: "contact-phone",
    },
    {
      icon: Clock,
      label: t("contact.card_hours_label"),
      primary: t("contact.hours_open"),
      secondary: t("contact.hours_closed"),
      cta: null,
      testid: "contact-hours",
    },
  ];

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-28 sm:py-40 bg-[#050505] overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/5 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="overline">{t("contact.overline")}</span>
          <h2
            className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] font-light"
            data-testid="contact-title"
          >
            {t("contact.title_1")}<br />
            <em className="shimmer-gold not-italic">{t("contact.title_em")}</em>
          </h2>
          <p className="mt-6 text-white/60 text-base sm:text-lg font-light">{t("contact.subtitle")}</p>
          <div className="mt-8 ornament">
            <div className="line" />
            <div className="diamond" />
            <div className="line" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-16 relative overflow-hidden border border-[#D4AF37]/40 p-10 sm:p-16 text-center"
          data-testid="contact-booking-card"
        >
          <div className="absolute inset-0 opacity-30">
            <img src={IMAGES.interior} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/80 to-black/95" />
          </div>
          <div className="relative">
            <p className="overline">{t("contact.booking_overline")}</p>
            <h3 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white font-light">
              {t("contact.booking_title")}
            </h3>
            <p className="mt-4 text-white/65 max-w-xl mx-auto text-sm sm:text-base">
              {t("contact.booking_description")}
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-book-btn"
              className="mt-10 inline-flex btn-gold"
            >
              {t("contact.booking_cta")}
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </a>
          </div>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          {items.map((item, i) => {
            const Icon = item.icon;
            const Inner = (
              <>
                <div className="flex items-center justify-between mb-6">
                  <Icon className="text-[#D4AF37]" size={22} strokeWidth={1.5} />
                  <span className="text-[10px] tracking-[0.32em] uppercase text-white/40">0{i + 1}</span>
                </div>
                <p className="overline mb-3">{item.label}</p>
                <p className="font-serif-display text-2xl text-white leading-tight">{item.primary}</p>
                <p className="text-white/55 text-sm mt-1">{item.secondary}</p>
                {item.cta && (
                  <span className="mt-6 inline-flex items-center gap-1.5 text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] border-b border-[#D4AF37]/30 pb-1 group-hover:border-[#D4AF37] transition-colors">
                    {item.cta} <ArrowUpRight size={12} strokeWidth={1.5} />
                  </span>
                )}
              </>
            );
            const className = "card-lux p-8 group flex flex-col h-full" + (item.href ? " cursor-pointer" : "");
            return item.href ? (
              <motion.a
                key={item.testid}
                href={item.href}
                target={item.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={className}
                data-testid={item.testid}
              >
                {Inner}
              </motion.a>
            ) : (
              <motion.div
                key={item.testid}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={className}
                data-testid={item.testid}
              >
                {Inner}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
